import { useEffect, useRef, useState } from "react";

// Every section below the hero on Home.jsx uses React.lazy(), which only
// splits the JS into separate chunks — it does NOT wait for scroll. As soon
// as the page loads, every chunk resolves and every section mounts at once,
// each running its own effects/timers/media-fetches immediately regardless
// of whether the visitor ever scrolls that far. On a throttled mobile
// connection all of that competes directly with the hero's own fonts/CSS/JS
// for the same bandwidth and main thread, which is the real reason this page
// has stayed slow even after fixing individual oversized assets one at a
// time — the eager audio downloads found in CaseStudy.jsx and
// CallSimulation.jsx were symptoms of this, not the whole cause, and more
// sections may have similar issues we haven't hit yet.
//
// This wrapper actually waits for scroll: it renders nothing (just a
// same-height placeholder, to avoid layout jump) until the section scrolls
// near the viewport, and only then mounts the real content — which is also
// the point React.lazy's own import() fires, so the JS chunk itself isn't
// even requested until it's needed either.
//
// minHeight (a plain px number) is a single value that can't track a
// section's own responsive breakpoint — several of these sections go from a
// multi-column grid on desktop to a stacked single column on mobile, which
// roughly doubles or triples their real height. A single guessed number was
// off by 100-500px at desktop width alone (measured against the production
// build), and the gap is larger on mobile once columns stack, which is what
// was producing a large, real layout shift (the whole rest of the page,
// footer included, jumping) every time a section resolved from placeholder
// to real content. minHeightClass takes Tailwind min-h-[...] utilities
// (optionally with a responsive prefix matching that section's own grid
// breakpoint, e.g. "min-h-[1500px] lg:min-h-[1019px]") so the placeholder
// can match each breakpoint instead of using one compromise number.
// anchorId: the id the wrapped section renders on its own root element (e.g.
// Pricing.jsx renders <section id="pricing">). A nav/footer link to "#pricing"
// only changes the URL hash and waits for that id to exist in the DOM to
// scroll to it (see ScrollToTop in App.js) — but a section behind LazyMount
// doesn't exist in the DOM at all until scroll brings it within rootMargin,
// which never happens for a hash jump from the top of the page. Passing the
// same id here as anchorId force-mounts the section the instant that hash is
// the current one (on load, and on hashchange for same-page clicks), so
// ScrollToTop's retry loop finds a real element to scroll to.
export default function LazyMount({ children, minHeight = 400, minHeightClass, rootMargin = "500px", anchorId }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(
    () => !!anchorId && typeof window !== "undefined" && window.location.hash === `#${anchorId}`
  );

  useEffect(() => {
    if (visible || !anchorId) return;
    const onHashChange = () => {
      if (window.location.hash === `#${anchorId}`) setVisible(true);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [visible, anchorId]);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, rootMargin]);

  return (
    <div
      ref={ref}
      className={visible ? undefined : minHeightClass}
      style={visible || minHeightClass ? undefined : { minHeight }}
    >
      {visible ? children : null}
    </div>
  );
}
